/**
 * Placeholder Pages for StarNexus Routes
 * These will be replaced with actual implementations in later tasks
 * @module components/starnexus/pages/PlaceholderPages
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';

// ============================================================================
// Dashboard Placeholder
// ============================================================================

export const DashboardPlaceholder: React.FC = () => (
  <SpaceBetween size="l">
    <Header variant="h1">Home Dashboard</Header>
    <Container header={<Header variant="h2">Welcome to StarNexus</Header>}>
      <Box padding="m">
        <SpaceBetween size="m">
          <Box variant="p">
            This is the home dashboard. It will display metrics, activity feed,
            quick actions, and processing snapshot.
          </Box>
          <Box color="text-status-info">Task 5 will implement this page.</Box>
        </SpaceBetween>
      </Box>
    </Container>
  </SpaceBetween>
);

// ============================================================================
// Dataset List Placeholder
// ============================================================================

export const DatasetListPlaceholder: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button
            variant="primary"
            onClick={() => navigate('/starnexus/datasets/create')}
          >
            Create Dataset
          </Button>
        }
      >
        Datasets
      </Header>
      <Container header={<Header variant="h2">All Datasets</Header>}>
        <Box padding="m">
          <SpaceBetween size="m">
            <Box variant="p">
              This page will display a table of all datasets with filtering,
              sorting, and pagination.
            </Box>
            <Box color="text-status-info">Task 7 will implement this page.</Box>
          </SpaceBetween>
        </Box>
      </Container>
    </SpaceBetween>
  );
};

// ============================================================================
// Create Dataset Placeholder
// ============================================================================

export const CreateDatasetPlaceholder: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Create New Dataset</Header>
      <Container header={<Header variant="h2">Dataset Creation Wizard</Header>}>
        <Box padding="m">
          <SpaceBetween size="m">
            <Box variant="p">
              This page will contain a 4-step wizard for creating new datasets:
              Upload Sample, AI Generation, Review Config, and Validation.
            </Box>
            <Box color="text-status-info">Task 9 will implement this page.</Box>
            <Button onClick={() => navigate('/starnexus/datasets')}>
              Back to Datasets
            </Button>
          </SpaceBetween>
        </Box>
      </Container>
    </SpaceBetween>
  );
};

// ============================================================================
// Dataset Detail Placeholder
// ============================================================================

export const DatasetDetailPlaceholder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Dataset: {id}</Header>
      <Container header={<Header variant="h2">Dataset Details</Header>}>
        <Box padding="m">
          <SpaceBetween size="m">
            <Box variant="p">
              This page will display detailed information about dataset "{id}"
              with tabs for Overview, Configuration, Processing History, Files,
              and Settings.
            </Box>
            <Box color="text-status-info">
              Task 11 will implement this page.
            </Box>
            <Button onClick={() => navigate('/starnexus/datasets')}>
              Back to Datasets
            </Button>
          </SpaceBetween>
        </Box>
      </Container>
    </SpaceBetween>
  );
};

// ============================================================================
// Processing Dashboard Placeholder
// ============================================================================

export const ProcessingDashboardPlaceholder: React.FC = () => (
  <SpaceBetween size="l">
    <Header variant="h1">File Processing</Header>
    <Container header={<Header variant="h2">Processing Dashboard</Header>}>
      <Box padding="m">
        <SpaceBetween size="m">
          <Box variant="p">
            This page will display global processing stats and per-dataset
            processing cards with real-time progress updates.
          </Box>
          <Box color="text-status-info">Task 13 will implement this page.</Box>
        </SpaceBetween>
      </Box>
    </Container>
  </SpaceBetween>
);

// ============================================================================
// Team Management Placeholder (Manager/Admin only)
// ============================================================================

export const TeamManagementPlaceholder: React.FC = () => (
  <SpaceBetween size="l">
    <Header variant="h1">Team Management</Header>
    <Container header={<Header variant="h2">Team Members</Header>}>
      <Box padding="m">
        <SpaceBetween size="m">
          <Alert type="info">
            This page is only accessible to users with Manager or Admin roles.
          </Alert>
          <Box variant="p">
            This page will display team members with role management and invite
            functionality.
          </Box>
          <Box color="text-status-info">Task 15 will implement this page.</Box>
        </SpaceBetween>
      </Box>
    </Container>
  </SpaceBetween>
);

// ============================================================================
// Monitoring Dashboard Placeholder (Admin only)
// ============================================================================

export const MonitoringDashboardPlaceholder: React.FC = () => (
  <SpaceBetween size="l">
    <Header variant="h1">System Monitoring</Header>
    <Container header={<Header variant="h2">Monitoring Dashboard</Header>}>
      <Box padding="m">
        <SpaceBetween size="m">
          <Alert type="info">
            This page is only accessible to users with Admin role.
          </Alert>
          <Box variant="p">
            This page will display system health, metrics charts, top datasets,
            and error logs.
          </Box>
          <Box color="text-status-info">Task 16 will implement this page.</Box>
        </SpaceBetween>
      </Box>
    </Container>
  </SpaceBetween>
);

// ============================================================================
// Settings Placeholder
// ============================================================================

export const SettingsPlaceholder: React.FC = () => (
  <SpaceBetween size="l">
    <Header variant="h1">Settings</Header>
    <Container header={<Header variant="h2">User Settings</Header>}>
      <Box padding="m">
        <SpaceBetween size="m">
          <Box variant="p">
            This page will contain user profile settings, notification
            preferences, and application settings.
          </Box>
          <Box color="text-status-info">
            Settings will be implemented as part of the dataset detail view
            (Task 11).
          </Box>
        </SpaceBetween>
      </Box>
    </Container>
  </SpaceBetween>
);

// ============================================================================
// Access Denied Page
// ============================================================================

export const AccessDeniedPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Access Denied</Header>
      <Container>
        <Box padding="m">
          <SpaceBetween size="m">
            <Alert type="error" header="Insufficient Permissions">
              You do not have permission to access this page. Please contact
              your administrator if you believe this is an error.
            </Alert>
            <Button variant="primary" onClick={() => navigate('/starnexus')}>
              Return to Dashboard
            </Button>
          </SpaceBetween>
        </Box>
      </Container>
    </SpaceBetween>
  );
};
